import { reactive } from 'vue'
import { apiDomain, port, useDevAPI } from '../../../config/config/settings.json'

// Two switches, deliberately separate (HAP-3654).
//
// HAP-3589 collapsed "stop the traffic" and "stop reading the flags" into one switch, and
// the cost was severe: the admin toggles kept writing to Mongo while the site rendered from
// a frozen snapshot. The board switched 13 of 14 elements off and 11 of them kept showing
// for a day. Telemetry going quiet must never cost us *control*, so these are now independent.
//
// FLAGS: element visibility. Must stay on — it is the only thing that makes the Campaign
// Command Center real. The traffic problem it caused was never the fetch itself, it was the
// `setInterval` re-polling every 15s for the life of every open tab, each poll re-reading the
// whole campaignElements collection server-side. That poll is gone: one fetch per page load,
// served from a short-lived in-memory cache on the API (see config/endpoints/campaignTrack.js).
const CAMPAIGN_FLAGS_ENABLED = true

// EVENTS: impression/click/dismiss beacons that feed the Performance and Funnel tabs.
// Bounded by real pageviews and deduped 60s per element client-side, so it is nothing like
// the old poll. Flip to false to silence the beacons *without* losing the on/off switches.
const CAMPAIGN_EVENTS_ENABLED = true

// Offline fallback, mirroring production at the time of writing. This is what visitors see
// for the few hundred ms before the live flags land, and what they keep seeing if the API is
// unreachable. It must stay conservative: an element that is off in prod belongs off here, or
// a slow API turns into a flash of an ad the board has already switched off.
// Regenerate with: curl -s https://api.churchandfamilylife.com/controllers/campaignTrack/elements
const FALLBACK_FLAGS = {
	'top-announcement-bar': false,
	'homepage-banner': false,
	'product-page-banner': false,
	'event-page-banner': false,
	'livestream-page-banner': false,
	'podcast-page-banner': false,
	'network-page-banner': false,
	'blog-sidebar-cta': false,
	'order-confirmation-banner': false,
	'footer-cta': false,
	'slidein-popup': true,
	'exit-intent-popup': false,
	'book-offer-popup': false,
	'order-email-block': false,
}

const state = reactive({
	// Never null. Consumers that render before the fetch resolves get the conservative
	// fallback rather than defaulting every unknown element to visible.
	flags: { ...FALLBACK_FLAGS },
	live: false,
	campaign: 'family-worship-kit',
	loadedAt: 0,
	loading: null,
})

// Re-exported so the mixin can $watch a getter against the module-level state
// directly. Composition-API computed via mixin setup() was not consistently
// re-tracking the dep on state.flags after the map loaded.
export const campaignFlagsState = state

function getApi() {
	try {
		const isDev = import.meta.env?.DEV
		return useDevAPI && isDev ? `http://localhost:${port}` : apiDomain
	} catch {
		return ''
	}
}

let apiBase = ''

export function configureCampaignFlags({ api }) {
	if (api) apiBase = api
}

async function loadFlags() {
	if (!CAMPAIGN_FLAGS_ENABLED) return state
	if (state.loading) return state.loading
	state.loading = (async () => {
		try {
			const base = apiBase || getApi()
			const res = await fetch(`${base}/controllers/campaignTrack/flags`, { credentials: 'omit' })
			const data = await res.json()
			const fetched = data?.flags
			// Only trust a map that actually looks like the element list. A hiccup that
			// returns `{}` or a stray HTML error page must not blank every flag, because
			// with fail-closed lookups below that would silently hide the whole campaign.
			if (fetched && typeof fetched === 'object' && Object.keys(fetched).length >= 5) {
				state.flags = fetched
				state.live = true
				state.loadedAt = Date.now()
			}
			state.campaign = data?.campaign || state.campaign
		} catch (e) {
			// Keep the last good map (or the conservative fallback). Never widen visibility
			// on error.
		} finally {
			state.loading = null
		}
		return state
	})()
	return state.loading
}

// Load once on module init in the browser, so the flags are in flight before any component
// mounts even if the mixin lives in a deferred chunk.
//
// There is deliberately no interval here. The 15s poll this replaces was the entire cause of
// the HAP-3589 load incident. Admin toggles now propagate on the visitor's next page load,
// which is the right trade for an ad placement: the API's cache TTL plus a navigation, versus
// a standing request every 15s from every open tab on the site.
if (typeof window !== 'undefined' && CAMPAIGN_FLAGS_ENABLED) {
	loadFlags()
}
// Expose for debugging from the browser console, tracking on or off.
if (typeof window !== 'undefined') {
	try { window.__cflCampaignFlags = state } catch {}
}

export function useCampaignFlags() {
	if (!state.loading && !state.live) loadFlags()
	return {
		state,
		// Fail CLOSED on an unknown name. The old fail-open (`!== false`) meant any element
		// missing from the map rendered unconditionally and could not be switched off at all
		// — that is exactly how book-offer-popup stayed on the site while reading OFF in the
		// admin. An ad we cannot control is an ad we should not show.
		isEnabled(name) {
			return state.flags?.[name] === true
		},
		reload: loadFlags,
	}
}

const inFlight = new Set()

export async function trackCampaignEvent({ element, type, route, meta, campaign }) {
	if (!CAMPAIGN_EVENTS_ENABLED) return
	if (!element || !type) return
	const key = `${type}:${element}`
	if (type === 'impression' && inFlight.has(key)) return
	inFlight.add(key)
	try {
		const base = apiBase || getApi()
		await fetch(`${base}/controllers/campaignTrack/event`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'omit',
			keepalive: true,
			body: JSON.stringify({
				element,
				type,
				// Callers may scope an event to a specific campaign (e.g. the RFW
				// landing funnel); otherwise fall back to the globally loaded campaign.
				campaign: campaign || state.campaign,
				route: route || (typeof window !== 'undefined' ? window.location.pathname : null),
				meta: meta || null,
			}),
		})
	} catch {}
	finally {
		// Allow another impression record after 60s for the same element (covers SPA navs)
		setTimeout(() => inFlight.delete(key), 60 * 1000)
	}
}
